import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import { AxiosError } from 'axios';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export type DatasetValidationResult = {
  valid: boolean;
  errors: string[];
  // Audio files with no matching transcript — kept, but skipped at train time.
  skipped: string[];
  // Reasons training cannot start, e.g. files still processing or missing
  // from storage. Every entry also makes ``valid`` false.
  blocked?: string[];
  // Warnings are non-blocking: things the customer should notice but that do
  // not stop training. Populated by the backend's ``build_readiness`` so the
  // panel can show them alongside the progress bar.
  warnings?: string[];
  // Progress fields from ``build_readiness`` (api-scriptix #4019). All
  // optional so a response cached before this shipped still parses.
  matched_pairs?: number;
  required_pairs?: number;
  total_duration_seconds?: number;
  required_duration_seconds?: number;
  uploaded_files?: number;
  contributed_clips?: number;
  clips_count?: number;
  clips_required?: number;
  distinct_anchor_terms?: number;
};

async function validateCustomModel(
  modelId: number
): Promise<DatasetValidationResult> {
  const { data } = await Axios.post<DatasetValidationResult>(
    `/api/v3/custom_models/${modelId}/validate`
  );
  return {
    valid: Boolean(data?.valid),
    errors: data?.errors ?? [],
    skipped: data?.skipped ?? [],
    blocked: data?.blocked ?? [],
    warnings: data?.warnings ?? [],
    matched_pairs: data?.matched_pairs,
    required_pairs: data?.required_pairs,
    total_duration_seconds: data?.total_duration_seconds,
    required_duration_seconds: data?.required_duration_seconds,
    uploaded_files: data?.uploaded_files,
    contributed_clips: data?.contributed_clips,
    clips_count: data?.clips_count,
    clips_required: data?.clips_required,
    distinct_anchor_terms: data?.distinct_anchor_terms
  };
}

export function useValidateCustomModel() {
  const { t } = useTranslation();

  return useMutation<DatasetValidationResult, AxiosError<BaseError>, number>(
    validateCustomModel,
    {
      onError: error => {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error_description ||
          t('validation_failed');
        toast.error(message);
      },
      useErrorBoundary: false
    }
  );
}

/**
 * Poll-friendly readiness query — same endpoint as the mutation above, but
 * as a ``useQuery`` so the page can show progress toward the training floor
 * before the customer presses Train. Failure is silent (the panel simply
 * hides the card) so a transient validate error doesn't drown the page in
 * red.
 */
export function useCustomModelReadiness(
  modelId: number,
  enabled: boolean = true
) {
  return useQuery<DatasetValidationResult, AxiosError<BaseError>>(
    ['custom-model-readiness', modelId],
    () => validateCustomModel(modelId),
    {
      enabled: enabled && Number.isFinite(modelId) && modelId > 0,
      // Contributions and uploads arrive asynchronously via the storage
      // uploader; refetch on a moderate cadence so the numbers keep up
      // with the dataset without hammering the endpoint.
      refetchInterval: 30_000,
      staleTime: 15_000,
      useErrorBoundary: false,
      // Never toast: the readiness card is informational, not a blocking
      // dialog, and a network blip must not create a red banner.
      onError: () => {}
    }
  );
}
